// Auth lifecycle:
// 1. Boot: check token expiry → silent-refresh if expired BEFORE fetching profile
// 2. Tab refocus: re-check expiry → refresh if needed
// 3. Profile error: logout ONLY when the BE actually rejected the session
//    (401/403). If the BE never answered — down, restarting, offline — the
//    session is still good, so we park on /connection-lost with tokens intact
//    instead of silently signing the user out.
// Single `status` state keeps re-renders minimal.

import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useGetAuthUserProfileQuery } from "@/services/authApi";
import {
  selectUser,
  setAuthChecked,
  setUserSlice,
} from "@/store/slices/user.slice";
import {
  decodeTokenExpiry,
  getAccessTokenMsRemaining,
  getAuthTokensFromLocalStorage,
  silentRefresh,
} from "@/utils/auth";
import { ENUM_AUTH_TOKENS, LOGOUT_USER_ACTION } from "@/utils/enums/auth.enums";
import { useAuthLogout } from "@/hooks/useAuthLogout";
import { goToConnectionLost } from "@/utils/connection-lost";

type AuthStatus = "checking" | "ready" | "unauthenticated";

// Only the BE explicitly refusing the token (401/403) proves the session is
// dead. FETCH_ERROR / TIMEOUT_ERROR / 5xx mean we never got a verdict.
const isSessionRejection = (error: unknown): boolean => {
  const status = (error as { status?: number | string } | undefined)?.status;
  return status === 401 || status === 403;
};

// If another tab refreshed while we were refreshing, the shared cookie now
// holds a token with plenty of life left — don't log out in that case.
const ANOTHER_TAB_REFRESHED_THRESHOLD_MS = 90_000;

const isTokenExpiredNow = (exp: number | null): boolean =>
  !exp || exp * 1000 <= Date.now();

export const useValidateAuth = () => {
  const dispatch = useDispatch();
  const authUser = useSelector(selectUser).user;
  const [status, setStatus] = useState<AuthStatus>("checking");
  const logout = useAuthLogout();

  // ── Boot check ────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const boot = async () => {
      const stored = getAuthTokensFromLocalStorage();
      const accessToken = stored?.[ENUM_AUTH_TOKENS.ACCESS_TOKEN];

      if (!accessToken) {
        if (!cancelled) setStatus("unauthenticated");
        dispatch({ type: LOGOUT_USER_ACTION.NAME });
        return;
      }

      const exp = decodeTokenExpiry(accessToken);
      if (!isTokenExpiredNow(exp)) {
        if (!cancelled) setStatus("ready");
        return;
      }

      // Token expired — refresh before firing the profile query
      const result = await silentRefresh();
      if (cancelled) return;

      // Another tab may have refreshed concurrently (lost rotation race but
      // cookie now holds a fresh token) — treat as success.
      const msRemaining = getAccessTokenMsRemaining();
      if (result.ok || (msRemaining ?? 0) > ANOTHER_TAB_REFRESHED_THRESHOLD_MS) {
        setStatus("ready");
        return;
      }

      // Server unreachable — the refresh token is probably still good. Park the
      // user instead of destroying the session.
      if (result.reason === "network") {
        goToConnectionLost();
        return;
      }

      await logout();
      if (!cancelled) setStatus("unauthenticated");
    };

    boot();
    return () => {
      cancelled = true;
    };
  }, [dispatch, logout]);

  // ── Tab visibility re-check ───────────────────────────────────────────────
  useEffect(() => {
    if (status !== "ready") return;

    const onVisible = async () => {
      if (document.visibilityState !== "visible") return;

      const msRemaining = getAccessTokenMsRemaining();
      if (msRemaining !== null && msRemaining > 0) return; // still valid

      const result = await silentRefresh(); // shared mutex
      if (result.ok) return;

      const after = getAccessTokenMsRemaining();
      if (after !== null && after > ANOTHER_TAB_REFRESHED_THRESHOLD_MS) return;

      if (result.reason === "network") {
        goToConnectionLost();
        return;
      }

      await logout();
    };

    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [status, logout]);

  // Re-evaluated each render. On logout, clearAuthTokens removes the cookie and
  // the LOGOUT dispatch re-renders this hook → hasToken flips false → query
  // stops and we stop "checking", so the guard redirects to login.
  const hasToken = Boolean(getAuthTokensFromLocalStorage());

  // ── Profile query — only after boot confirms a valid/refreshed token ───────
  const {
    data: user,
    isFetching,
    isError,
    error,
  } = useGetAuthUserProfileQuery(undefined, {
    skip: status !== "ready" || !hasToken,
  });

  useEffect(() => {
    if (status !== "ready" || !hasToken || isFetching) return;

    if (isError) {
      // The BE said "no" → real logout. Anything else (server down/restarting)
      // → keep the session and show the connection-lost screen.
      if (isSessionRejection(error)) logout();
      else goToConnectionLost();
      return;
    }

    if (user?.data) dispatch(setUserSlice({ user: user.data }));
    dispatch(setAuthChecked(true));
  }, [status, hasToken, isFetching, user, isError, error, logout, dispatch]);

  return {
    // Only spin while genuinely resolving auth — never when the token is gone
    // (manual logout), so the guard can redirect immediately.
    isChecking:
      status === "checking" ||
      (status === "ready" &&
        hasToken &&
        (isFetching || (!!user?.data && !authUser))),
    isAuthenticated: !!authUser,
  };
};
